import { Button } from "@components/common/button";
import { DictType } from "@pma/types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@components/common/alert-dialog";
import axios from "axios";
import { parseCookies } from "nookies";
import { useMutation } from "@tanstack/react-query";
import { toast } from "@hooks/use-toast";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import {
  SERVER_URL,
  ORGANIZATION_REMOVE_USER_ENDPOINT,
} from "@pma/utils/api-routes";
import { Trash2Icon } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@components/common/tooltip";
import useTranslator from "@hooks/use-translator";
import { HTMLAttributes } from "react";
import { USERS_PAGE_ROUTE } from "@pma/utils/page-routes";
import { useAuth } from "@pma/stores/auth-store";

interface Props extends HTMLAttributes<HTMLDivElement> {
  user: DictType;
}

const RemoveUserFromOrganization = ({ user }: Props) => {
  const { translate } = useTranslator();
  const { authConfig } = useAuth();
  const accessToken = parseCookies()[ACCESS_TOKEN];
  const navigate = useNavigate();

  const paramUserId = useParams();

  const orgId = authConfig?.last_organization_id;

  const removeUser = async () => {
    const response = await axios.delete(
      SERVER_URL +
        ORGANIZATION_REMOVE_USER_ENDPOINT +
        `/${user?.id || paramUserId?.userId}`,
      {
        data: {
          organization_id: orgId,
        },

        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const removeUserMutation = useMutation({
    mutationKey: ["remove-user", user?.id],
    mutationFn: removeUser,
    onSuccess: (data) => {
      navigate(USERS_PAGE_ROUTE);

      toast({
        title: translate("Success"),
        description: `${user?.name || "User"} ${translate("has been removed")}`,
        variant: "success",
      });
    },
    onError: (error) => {
      toast({
        title: translate("Error"),
        description: `${translate("Unable to remove")} ${user?.name || "user"} ${translate("from organization")}`,
        variant: "error",
      });
    },
  });

  return (
    <AlertDialog>
      <AlertDialogTrigger>
        <Tooltip>
          <TooltipTrigger>
            <Button
              variant={"outline"}
              className="flex flex-col rounded-md hover:bg-slate-300 text-red-400 items-center justify-center"
            >
              {translate("Remove from organization")} <Trash2Icon size={14} />
            </Button>
          </TooltipTrigger>
          <TooltipContent className="flex bg-destructive items-center gap-1">
            <h4>
              {translate(`Remove`)} {user?.name}
            </h4>
          </TooltipContent>
        </Tooltip>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {translate("Proceed to remove")} {user?.name}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            {translate("This will remove the user from the organization")}.{" "}
            <br />
            {translate(
              "To grant them access again you will need to re-invite them",
            )}
            .{" "}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{translate("Cancel")}</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive"
            disabled={removeUserMutation?.isPending}
            onClick={() => removeUserMutation.mutate()}
          >
            {translate("Continue")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default RemoveUserFromOrganization;
